import { useState } from "react";
import { Pin, Trash2, Paperclip, Reply, ChevronDown, ChevronRight } from "lucide-react";
import { clsx } from "clsx";
import DOMPurify from "dompurify";
import type { ProjectMessage, ConversationType, MentionUser } from "../../types";
import {
  useDeleteProjectMessage,
  usePinProjectMessage,
  type ConversationBase,
} from "../../api/conversationsApi";
import { MessageComposer } from "./MessageComposer";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { highlightMentions, highlightMentionsInHtml } from "@/utils/highlightMentions";
import { formatDateTime } from "@/utils/formatDate";

interface Props {
  message: ProjectMessage;
  projectId: string;
  conversationType: ConversationType;
  currentUserId: string;
  isCeo: boolean;
  mentionUsers: MentionUser[];
  isReply?: boolean;
  base?: ConversationBase;
}

function isHtmlContent(content: string) {
  return content.trimStart().startsWith("<");
}

function sanitize(html: string) {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ["p", "br", "strong", "em", "u", "ul", "ol", "li", "h1", "h2", "h3", "a"],
    ALLOWED_ATTR: ["start", "href", "target", "rel", "class"],
  });
}

function MessageContent({ content, mentionUsers }: { content: string; mentionUsers: MentionUser[] }) {
  const names = mentionUsers.map((u) => u.name);
  if (isHtmlContent(content)) {
    return (
      <div
        className="rich-text-content text-sm text-gray-800 dark:text-gray-200 mt-0.5"
        dangerouslySetInnerHTML={{ __html: highlightMentionsInHtml(sanitize(content), names) }}
      />
    );
  }
  return (
    <p className="text-sm text-gray-800 dark:text-gray-200 mt-0.5 whitespace-pre-wrap break-words">
      {highlightMentions(content, names)}
    </p>
  );
}

export function MessageItem({
  message,
  projectId,
  conversationType,
  currentUserId,
  isCeo,
  mentionUsers,
  isReply = false,
  base = "projects",
}: Props) {
  const [replying, setReplying] = useState(false);
  const [showReplies, setShowReplies] = useState(true);

  const canDelete = isCeo || String(message.author) === currentUserId;
  const deleteMut = useDeleteProjectMessage();
  const pinMut = usePinProjectMessage();

  return (
    <div className={clsx("group", isReply ? "ml-8 mt-2" : "")}>
      <div
        className={clsx(
          "flex gap-3 p-3 rounded-xl transition-colors",
          message.is_pinned
            ? "bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/40"
            : "hover:bg-gray-50 dark:hover:bg-gray-800/50",
        )}
      >
        {/* Avatar */}
        <UserAvatar
          fullName={message.author_name}
          profilePictureUrl={message.author_profile_picture_url}
          userId={message.author}
          size="md"
        />

        <div className="flex-1 min-w-0">
          {/* Header */}
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              {message.author_name}
            </span>
            <span className="text-xs text-gray-400 dark:text-gray-500">
              {formatDateTime(message.created_at)}
            </span>
            {message.is_pinned && (
              <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-0.5">
                <Pin size={10} />
                Przypięta
              </span>
            )}
          </div>

          {/* Content */}
          <MessageContent content={message.content} mentionUsers={mentionUsers} />

          {/* Attachments */}
          {message.attachments.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-2">
              {message.attachments.map((att) => (
                <a
                  key={att.id}
                  href={att.file_url ?? "#"}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1.5 text-xs text-[var(--color-primary)] bg-[var(--color-primary-dim)] px-2 py-1 rounded-lg hover:bg-[var(--color-primary)]/20 transition-colors"
                >
                  <Paperclip size={11} />
                  <span className="max-w-[180px] truncate">{att.name}</span>
                </a>
              ))}
            </div>
          )}

          {/* Action bar */}
          <div className="flex items-center gap-1 mt-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            {!isReply && (
              <button
                onClick={() => setReplying((v) => !v)}
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-[var(--color-primary)] px-1.5 py-0.5 rounded transition-colors"
              >
                <Reply size={12} />
                Odpowiedz
              </button>
            )}
            {isCeo && (
              <button
                onClick={() =>
                  pinMut.mutate({
                    projectId,
                    messageId: message.id,
                    conversationType,
                    base,
                  })
                }
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-amber-500 px-1.5 py-0.5 rounded transition-colors"
                title={message.is_pinned ? "Odepnij" : "Przypiij"}
              >
                <Pin size={12} />
              </button>
            )}
            {canDelete && (
              <button
                onClick={() =>
                  deleteMut.mutate({
                    projectId,
                    messageId: message.id,
                    conversationType,
                    base,
                  })
                }
                className="flex items-center gap-1 text-xs text-gray-400 hover:text-red-500 px-1.5 py-0.5 rounded transition-colors"
                title="Usuń"
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>

          {/* Reply composer */}
          {replying && (
            <div className="mt-2">
              <MessageComposer
                projectId={projectId}
                conversationType={conversationType}
                mentionUsers={mentionUsers}
                base={base}
                parentId={message.id}
                onPosted={() => setReplying(false)}
                placeholder="Odpowiedz…"
              />
            </div>
          )}
        </div>
      </div>

      {/* Replies */}
      {!isReply && message.replies.length > 0 && (
        <div className="ml-8 mt-1">
          <button
            onClick={() => setShowReplies((v) => !v)}
            className="flex items-center gap-1 text-xs text-gray-400 hover:text-[var(--color-primary)] mb-1 px-1"
          >
            {showReplies ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {message.replies.length}{" "}
            {message.replies.length === 1 ? "odpowiedź" : "odpowiedzi"}
          </button>
          {showReplies && (
            <div className="border-l-2 border-gray-100 dark:border-gray-700 pl-3 space-y-1">
              {message.replies.map((reply) => (
                <MessageItem
                  key={reply.id}
                  message={reply}
                  projectId={projectId}
                  conversationType={conversationType}
                  currentUserId={currentUserId}
                  isCeo={isCeo}
                  mentionUsers={mentionUsers}
                  base={base}
                  isReply
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
